/**
 * Peace EQ - Modern Main Entry Point
 * Refactored to follow Single Responsibility Principle
 * 
 * This simplified main.ts delegates responsibilities to focused modules:
 * - AppInitializer: Complete application setup and initialization
 * - ServiceRegistry: Service registration and dependency injection  
 * - LegacyWindowExports: Minimal window object exports for compatibility
 */

// Import CSS and types
import './style.css';
import './types/index';

// Import focused modules
import { AppInitializer } from './core/app-initializer';
import { Logger } from './core/logger';
import { container } from './core/service-container';
import { getErrorHandler } from './core/error-handling';

const logger = Logger.createComponentLogger('main');

// Application initializer instance
let appInitializer: AppInitializer;

/**
 * Initialize the Peace EQ application using the modern architecture
 */
async function initializeApplication(): Promise<void> {
    try {
        logger.info("Peace EQ Application starting...");
        
        appInitializer = new AppInitializer();
        await appInitializer.initialize();
        
        logger.info("Peace EQ Application started successfully");
    } catch (error) {
        getErrorHandler().handleErrorWithNotification(error, 'main', 'initializeApplication', 'Failed to start Peace EQ application. Please refresh the page and try again.');
        throw error;
    }
}

/**
 * Start application when DOM is ready
 */
function startApplication(): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initializeApplication().catch(error => {
                console.error('Application startup failed:', error);
            });
        });
    } else {
        initializeApplication().catch(error => {
            console.error('Application startup failed:', error);
        });
    }
}

// Start the application
startApplication();

// Export for potential module usage and testing
export { appInitializer, container };
